import { cn } from "@/utils/theme";
import { useWorkflowStore } from "@/workflow_api/store/workflowStore";
import type { NodeData } from "@/workflow_api/types/workflow";
import { VariableKeyBox } from "../key-box";
import { Icon } from "@elaniintc/goes-design-system";
import { useGetEntity } from "@/queries/digital-files/entities.api";
import { useMemo } from "react";

interface Props {
  id: string;
}

/**
 * Map entity field types to variable display types
 */
const mapFieldTypeToVariableType = (
  fieldType: string,
): "string" | "number" | "boolean" | "array" | "date" => {
  const type = fieldType.toLowerCase();
  if (
    [
      "text",
      "textarea",
      "email",
      "phone",
      "dui_nit",
      "string",
      "select",
    ].includes(type)
  ) {
    return "string";
  }
  if (["date", "datetime", "timestamp"].includes(type)) {
    return "date";
  }
  if (["integer", "number", "float", "decimal"].includes(type)) {
    return "number";
  }
  if (["boolean", "switch"].includes(type)) {
    return "boolean";
  }
  if (["array", "list", "file"].includes(type)) {
    return "array";
  }
  return "string";
};

const Content = ({
  data,
  entityId,
}: {
  data: NodeData;
  entityId: string | undefined;
}) => {
  // Get operation from inputs
  const operation = useMemo(() => {
    const operationInput = data.inputs.find(
      (input) => input.name === "operation",
    );
    return ((operationInput?.value as string) || "").toLowerCase();
  }, [data.inputs]);

  // Fetch entity with fields
  const { data: entityData, isLoading: isLoadingEntity } = useGetEntity(
    entityId || "",
    { includeFields: true },
    !!entityId,
  );

  // Get entity fields for read/create/update operations
  const entityFields = useMemo(() => {
    if (
      !entityData?.fields ||
      !["read", "create", "update"].includes(operation)
    ) {
      return [];
    }
    return entityData.fields.map((field) => ({
      name: field.name,
      type: mapFieldTypeToVariableType(field.field_type),
    }));
  }, [entityData, operation]);

  if (!data) return null;

  // Show loading if fetching entity data
  if (isLoadingEntity && entityId) {
    return (
      <div className="flex items-center justify-center py-4">
        <Icon
          name="spinner-third"
          className="animate-spin text-sm text-on-disabled"
          type="far"
        />
      </div>
    );
  }

  // Base variables that are always returned
  const baseVariables: Array<{
    name: string;
    type: "string" | "number" | "boolean" | "array" | "date";
  }> = [
    { name: "ID", type: "string" },
    { name: "Código único", type: "string" },
  ];

  // Add name for most operations
  if (
    ["read", "create", "update", "link_users", "unlink_users"].includes(
      operation,
    )
  ) {
    baseVariables.push({ name: "Nombre", type: "string" });
  }

  // Add user IDs for link/unlink operations
  if (operation === "link_users") {
    baseVariables.push({ name: "IDs usuarios asociados", type: "array" });
  } else if (operation === "unlink_users") {
    baseVariables.push({ name: "IDs usuarios desasociados", type: "array" });
  }

  return (
    <div className="flex">
      <div
        className={cn(
          "w-0.5 relative translate-x-6.25",
          "bg-neutral-object-accent",
        )}
      >
        <span className="absolute left-3 top-3.5 w-2 h-2 bg-neutral-object-accent rounded-full"></span>
        <span className="absolute w-3 h-5 border-l-2 border-t-2 border-neutral-object-accent rotate-x-180 rounded-tl-md"></span>
      </div>

      <div className="relative pl-12.5 w-full">
        <div className="py-1">
          <div className="space-y-3">
            {/* Base variables */}
            {baseVariables.map((variable, index) => (
              <VariableKeyBox
                key={`base-var-${index}`}
                name={variable.name}
                type={variable.type}
              />
            ))}
            {/* Entity field variables */}
            {entityFields.map((field, index) => (
              <VariableKeyBox
                key={`field-var-${index}`}
                name={field.name}
                type={field.type}
              />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export const ExpedienteVariables = ({ id }: Props) => {
  const { getNodeById } = useWorkflowStore();
  const node = getNodeById(id);

  // Get entity ID from node inputs - call hook unconditionally
  const entityId = useMemo(() => {
    if (!node) return undefined;
    const nodeData = node.data as unknown as NodeData;
    const entityIdInput = nodeData.inputs.find(
      (input) => input.name === "expedient_base_entity_id",
    );
    return entityIdInput?.value as string | undefined;
  }, [node]);

  if (!node) return <></>;
  const nodeData = node.data as unknown as NodeData;

  return <Content data={nodeData} entityId={entityId} />;
};
